// src/features/MediaLibrary/AudioPlayer.js
import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import AudioService from './AudioService';
import globalAudioManager from './GlobalAudioManager';

/**
 * Shared audio player used across all unit types.
 *
 * Two usage modes:
 *   <AudioPlayer audioId="abc123" />           — looks up URL via AudioService (preferred)
 *   <AudioPlayer audioUrl="https://..." />      — uses URL directly, skips Firestore lookup
 *
 * Optional callbacks:
 *   onPlayStart  — called when playback actually begins
 *   showFileName — show the asset filename above the button (admin mode only)
 */
const AudioPlayer = ({
  audioId,
  audioUrl,
  onPlayStart,
  showFileName = false,
  className = '',
  style = {}
}) => {
  const [playState, setPlayState] = useState('stopped'); // 'stopped' | 'loading' | 'playing' | 'error'
  const [audioData, setAudioData] = useState(null);
  const [error, setError] = useState(false);
  const audioRef = useRef(null);
  const componentMounted = useRef(true);
  const playerId = useRef(`player-${audioId || 'url'}-${Date.now()}`);

  // Cleanup on unmount
  useEffect(() => {
    componentMounted.current = true;
    return () => {
      componentMounted.current = false;
    };
  }, []);

  // Load audio data — either from Firestore via audioId, or directly from audioUrl
  useEffect(() => {
    // Direct URL mode — no Firestore lookup needed
    if (audioUrl) {
      setAudioData({ url: audioUrl, name: null });
      setError(false);
      setPlayState('stopped');
      return;
    }

    if (!audioId) return;

    let cancelled = false;
    setAudioData(null);
    setError(false);
    setPlayState('stopped');

    const loadAudioData = async () => {
      try {
        const data = await AudioService.getAudioAsset(audioId);
        if (!cancelled) {
          setAudioData(data);
          setError(false);
        }
      } catch (err) {
        console.error('AudioPlayer error loading asset:', err);
        if (!cancelled) {
          setError(true);
          setPlayState('error');
        }
      }
    };

    loadAudioData();
    return () => { cancelled = true; };
  }, [audioId, audioUrl]);

  // Register with GlobalAudioManager so only one player plays at a time
  useEffect(() => {
    const stopCallback = () => {
      if (componentMounted.current) {
        setPlayState('stopped');
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }
      }
    };

    globalAudioManager.register(playerId.current, stopCallback);

    return () => {
      globalAudioManager.unregister(playerId.current);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Handle button click — toggle play/pause
  const handleClick = useCallback(async () => {
    if (!audioData || !audioData.url || playState === 'loading') return;

    if (playState === 'playing') {
      if (audioRef.current) {
        audioRef.current.pause();
        setPlayState('stopped');
        globalAudioManager.onStop(playerId.current);
      }
    } else {
      try {
        setPlayState('loading');
        globalAudioManager.onPlay(playerId.current);

        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current = null;
        }

        const audio = new Audio(audioData.url);
        audioRef.current = audio;

        audio.addEventListener('canplay', () => {
          if (componentMounted.current) {
            setPlayState('playing');
            if (onPlayStart) {
              onPlayStart();
            }
          }
        });

        audio.addEventListener('ended', () => {
          if (componentMounted.current) {
            setPlayState('stopped');
            globalAudioManager.onStop(playerId.current);
          }
        });

        audio.addEventListener('error', () => {
          if (componentMounted.current) {
            setError(true);
            setPlayState('error');
            globalAudioManager.onStop(playerId.current);
          }
        });

        await audio.play();
      } catch (err) {
        if (componentMounted.current) {
          setError(true);
          setPlayState('error');
        }
      }
    }
  }, [audioData, playState, onPlayStart]);

  // Loading state while fetching from Firestore
  if (!audioData && !error) {
    return (
      <div style={{ fontSize: '12px', color: '#888', padding: '4px 8px' }}>
        ⏳ Loading audio...
      </div>
    );
  }

  if (error || !audioData) {
    return (
      <div style={{ fontSize: '12px', color: '#e53935', padding: '4px 8px' }}>
        ⚠️ Audio unavailable
      </div>
    );
  }

  const getButtonContent = () => {
    switch (playState) {
      case 'loading': return { icon: '⏳', text: 'Loading...' };
      case 'playing': return { icon: '⏸️', text: 'Pause' };
      case 'error':   return { icon: '⚠️', text: 'Error' };
      default:        return { icon: '▶️', text: 'Play' };
    }
  };

  const getButtonColor = () => {
    switch (playState) {
      case 'playing': return '#e8a327'; // amber — pause state
      case 'error':   return '#ff4747'; // red
      case 'loading': return '#6c757d'; // grey
      default:        return '#19a4a0'; // teal — play state
    }
  };

  const buttonContent = getButtonContent();
  const displayName = showFileName && audioData.name ? audioData.name : null;

  const buttonStyle = {
    backgroundColor: getButtonColor(),
    color: 'white',
    border: 'none',
    borderRadius: '20px',
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: playState === 'loading' ? 'not-allowed' : 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    minWidth: '100px',
    height: '40px',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    ...style
  };

  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}
         className={className}>
      {displayName && (
        <div style={{ fontSize: '12px', color: '#666', fontFamily: 'monospace', textAlign: 'center' }}>
          {displayName}
        </div>
      )}
      <button
        onClick={handleClick}
        disabled={playState === 'loading'}
        style={buttonStyle}
        title={`${buttonContent.text} audio`}
      >
        <span>{buttonContent.icon}</span>
        <span>{buttonContent.text}</span>
      </button>
    </div>
  );
};

export default memo(AudioPlayer);